import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, DollarSign, Users, Send, Eye
} from 'lucide-react';
import {
  getDeal, getOpportunities, createOpportunity, updateOpportunity,
  getCommitments, createCommitment, getContacts
} from '../../services/adminDealFlowApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/new-components/ui/tabs';
import { Input } from '../../components/new-components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/new-components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/new-components/ui/select';

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const STAGES = [
  { key: 'prospect', label: 'Prospects', color: 'bg-muted' },
  { key: 'interested', label: 'Interested', color: 'bg-blue-100' },
  { key: 'reviewing', label: 'Reviewing', color: 'bg-amber-100' },
  { key: 'committing', label: 'Committing', color: 'bg-purple-100' },
  { key: 'complete', label: 'Complete', color: 'bg-green-100' },
];

const stageColorMap: Record<string,string> = {
  prospect: 'bg-muted text-muted-foreground',
  interested: 'bg-blue-50 text-blue-700',
  reviewing: 'bg-amber-50 text-amber-700',
  committing: 'bg-purple-50 text-purple-700',
  complete: 'bg-green-50 text-green-700',
};

const CapitalRaiseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [deal, setDeal] = useState<any>(null);
  const [pipeline, setPipeline] = useState<any>({});
  const [commitments, setCommitments] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('opportunities');
  const [viewMode, setViewMode] = useState<'pipeline' | 'grid'>('pipeline');
  const [showNewOpp, setShowNewOpp] = useState(false);
  const [showCommitment, setShowCommitment] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState<any>(null);
  const [newOpp, setNewOpp] = useState({ contact_id: '', expected_amount: '', stage: 'prospect' });
  const [newCommit, setNewCommit] = useState({ amount: '', class_name: 'Class A', account_name: '' });

  const load = async () => {
    if (!id) return;
    try {
      const [dealRes, pipeRes, commitsRes, contactsRes]: any[] = await Promise.all([
        getDeal(id),
        getOpportunities(id, { view: 'pipeline' }),
        getCommitments(id),
        getContacts({ page: 1 })
      ]);
      setDeal(dealRes.data.data);
      setPipeline(pipeRes.data.data || {});
      setCommitments(commitsRes.data.data || []);
      setContacts(contactsRes.data.data || []);
    } catch (err) {
      console.error('Failed to load deal:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const handleCreateOpp = async () => {
    try {
      await createOpportunity({
        deal_id: id!,
        contact_id: newOpp.contact_id || undefined,
        expected_amount: Number(newOpp.expected_amount) || 0,
        stage: newOpp.stage,
      });
      setShowNewOpp(false);
      setNewOpp({ contact_id: '', expected_amount: '', stage: 'prospect' });
      load();
    } catch (err) { console.error(err); }
  };

  const handleMoveStage = async (oppId: string, newStage: string) => {
    try {
      await updateOpportunity(oppId, { stage: newStage });
      load();
    } catch (err) { console.error(err); }
  };

  const handleRecordCommitment = async () => {
    if (!selectedOpp) return;
    try {
      await createCommitment({
        opportunity_id: selectedOpp.id,
        deal_id: id!,
        contact_id: selectedOpp.contact_id,
        amount: Number(newCommit.amount) || 0,
        account_name: newCommit.account_name,
      });
      setShowCommitment(false);
      setNewCommit({ amount: '', class_name: 'Class A', account_name: '' });
      setSelectedOpp(null);
      load();
    } catch (err) { console.error(err); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!deal) return <div className="text-muted-foreground text-center py-12">Deal not found</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-[#F0F0F0] rounded-lg flex items-center justify-center border border-border">
          <span className="text-lg font-bold text-[hsl(var(--chart-2))]">
            {deal.title?.substring(0, 2).toUpperCase()}
          </span>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-display font-semibold text-[#242424]">{deal.title}</h1>
            <Badge variant={deal.status === 'open' ? 'default' : 'secondary'}>
              {deal.status === 'open' ? 'Active' : deal.status}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">{deal.summary}</p>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex gap-3">
        <Link to="/admin/capital-raises">
          <Button variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back To Capital Raises
          </Button>
        </Link>
        <Link to={`/admin/projects/${id}`}>
          <Button variant="outline">
            <Eye className="h-4 w-4 mr-2" /> Go To Project
          </Button>
        </Link>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="opportunities">Opportunities</TabsTrigger>
          <TabsTrigger value="commitments">Commitments</TabsTrigger>
          <TabsTrigger value="deal-room">Deal Room</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
            <Card className="border-border">
              <CardContent className="p-5">
                <p className="text-sm text-muted-foreground mb-1">Raise Target</p>
                <p className="text-2xl font-display font-semibold text-[#242424]">
                  {formatCurrency(Number(deal.raise_target_cents || 0) / 100)}
                </p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="p-5">
                <p className="text-sm text-muted-foreground mb-1">Total Raised</p>
                <p className="text-2xl font-display font-semibold text-[hsl(var(--success))]">
                  {formatCurrency(Number(deal.raised_cents || 0) / 100)}
                </p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="p-5">
                <p className="text-sm text-muted-foreground mb-1">Share Classes</p>
                <p className="text-2xl font-display font-semibold text-[hsl(var(--chart-2))]">
                  {deal.classes?.length || 0}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Classes */}
          {deal.classes && deal.classes.length > 0 && (
            <Card className="border-border mt-4">
              <CardHeader>
                <CardTitle className="text-[#242424]">Share Classes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {deal.classes.map((cls: any) => (
                    <div key={cls.id} className="p-4 bg-[#F0F0F0] rounded-lg">
                      <p className="font-semibold text-[#242424]">{cls.name}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Min: {formatCurrency(Number(cls.min_invest_cents || 0) / 100)}
                        {cls.max_invest_cents && ` — Max: ${formatCurrency(Number(cls.max_invest_cents) / 100)}`}
                      </p>
                      {cls.terms_json && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          {typeof cls.terms_json === 'string' ? cls.terms_json : JSON.stringify(cls.terms_json)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Opportunities Tab - Pipeline View */}
        <TabsContent value="opportunities">
          <div className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex gap-2">
                <Button
                  variant={viewMode === 'pipeline' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('pipeline')}
                >
                  Pipeline
                </Button>
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                >
                  Grid
                </Button>
              </div>
              <Button onClick={() => setShowNewOpp(true)}>
                <Plus className="h-4 w-4 mr-2" /> New Opportunity
              </Button>
            </div>

            {/* Pipeline Kanban */}
            <div className="flex gap-4 overflow-x-auto pb-4">
              {STAGES.map(stage => {
                const stageData = pipeline[stage.key] || { opportunities: [], count: 0, total_cents: 0 };
                return (
                  <div key={stage.key} className="min-w-[280px] flex-shrink-0">
                    <div className="flex items-center justify-between mb-3 px-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[#242424] uppercase">{stage.label}</span>
                        <Badge variant="secondary" className="text-xs">{stageData.count}</Badge>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {formatCurrency(stageData.total_cents / 100)}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {stageData.opportunities.map((opp: any) => (
                        <div
                          key={opp.id}
                          className="bg-card border border-border rounded-lg p-4 cursor-pointer hover:border-foreground/30 transition"
                          onClick={() => setSelectedOpp(opp)}
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-8 h-8 bg-[#F0F0F0] rounded-full flex items-center justify-center">
                              <Users className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#242424] truncate">
                                {opp.contact_email || 'Unknown Contact'}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(opp.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <p className="text-lg font-semibold text-[#242424]">
                            {formatCurrency(Number(opp.expected_amount_cents || 0) / 100)}
                          </p>

                          {/* Stage move buttons */}
                          <div className="flex gap-1 mt-3 flex-wrap">
                            {STAGES.filter(s => s.key !== stage.key).map(s => (
                              <button
                                key={s.key}
                                onClick={(e) => { e.stopPropagation(); handleMoveStage(opp.id, s.key); }}
                                className={`text-xs px-2 py-1 rounded ${stageColorMap[s.key]} opacity-70 hover:opacity-100 transition`}
                              >
                                → {s.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* Commitments Tab */}
        <TabsContent value="commitments">
          <div className="mt-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
              <h3 className="text-lg font-display font-semibold text-[#242424]">Commitments</h3>
              <Button onClick={() => { setShowCommitment(true); setSelectedOpp(null); }} className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" /> Record Commitment
              </Button>
            </div>
            <Card className="border-border">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full min-w-[720px]">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-4 text-xs text-muted-foreground font-medium">Contact</th>
                      <th className="text-left p-4 text-xs text-muted-foreground font-medium">Class</th>
                      <th className="text-left p-4 text-xs text-muted-foreground font-medium">Account</th>
                      <th className="text-right p-4 text-xs text-muted-foreground font-medium">Amount</th>
                      <th className="text-left p-4 text-xs text-muted-foreground font-medium">Status</th>
                      <th className="text-left p-4 text-xs text-muted-foreground font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commitments.map((c: any) => (
                      <tr key={c.id} className="border-b border-border hover:bg-muted/50">
                        <td className="p-4 text-sm text-[#242424]">{c.contact_email || '—'}</td>
                        <td className="p-4 text-sm text-muted-foreground">{c.class_name || 'Class A'}</td>
                        <td className="p-4 text-sm text-muted-foreground">{c.account_name || '—'}</td>
                        <td className="p-4 text-sm text-[#242424] text-right font-medium">
                          {formatCurrency(Number(c.amount_cents || 0) / 100)}
                        </td>
                        <td className="p-4">
                          <Badge variant={c.status === 'funded' ? 'default' : 'secondary'} className="text-xs">
                            {c.status}
                          </Badge>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {new Date(c.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                    {commitments.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-muted-foreground">No commitments yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Deal Room Tab */}
        <TabsContent value="deal-room">
          <div className="mt-4 text-center py-12 text-muted-foreground">
            <p>Deal room document management coming soon</p>
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <div className="mt-4 text-center py-12 text-muted-foreground">
            <p>Deal settings and configuration</p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Opportunity Dialog */}
      <Dialog open={showNewOpp} onOpenChange={setShowNewOpp}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>New Opportunity</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Contact</label>
              <Select value={newOpp.contact_id} onValueChange={v => setNewOpp(p => ({ ...p, contact_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select contact..." />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map((c: any) => (
                    <SelectItem key={c.id} value={c.id}>{c.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Expected Amount ($)</label>
              <Input
                type="number"
                value={newOpp.expected_amount}
                onChange={e => setNewOpp(p => ({ ...p, expected_amount: e.target.value }))}
                placeholder="100000"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Stage</label>
              <Select value={newOpp.stage} onValueChange={v => setNewOpp(p => ({ ...p, stage: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map(s => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewOpp(false)}>Cancel</Button>
            <Button onClick={handleCreateOpp}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Commitment Dialog */}
      <Dialog open={showCommitment} onOpenChange={setShowCommitment}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Manually Record Commitment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Class A Amount *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  value={newCommit.amount}
                  onChange={e => setNewCommit(p => ({ ...p, amount: e.target.value }))}
                  className="pl-8"
                  placeholder="100000"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Class *</label>
              <Select value={newCommit.class_name} onValueChange={v => setNewCommit(p => ({ ...p, class_name: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Class A">Class A</SelectItem>
                  <SelectItem value="Class B">Class B</SelectItem>
                  <SelectItem value="Class C">Class C</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Choose an account *</label>
              <Select value={newCommit.account_name} onValueChange={v => setNewCommit(p => ({ ...p, account_name: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose an account..." />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map((c: any) => (
                    <SelectItem key={c.id} value={c.email}>{c.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommitment(false)}>Cancel</Button>
            <Button onClick={handleRecordCommitment} disabled={!newCommit.amount}>Record Commitment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Selected Opportunity Side Panel */}
      {selectedOpp && !showCommitment && (
        <div className="fixed right-0 top-0 h-full w-full max-w-md bg-card border-l border-border z-50 p-4 sm:p-6 overflow-y-auto shadow-lg">
          <div className="flex items-center justify-between gap-3 mb-6">
            <h3 className="text-lg font-display font-semibold text-[#242424]">Opportunity Detail</h3>
            <button onClick={() => setSelectedOpp(null)} className="text-muted-foreground hover:text-[#242424]">✕</button>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-xs text-muted-foreground">Contact</p>
              <p className="text-sm text-[#242424]">{selectedOpp.contact_email || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Expected Amount</p>
              <p className="text-lg font-bold text-[#242424]">
                {formatCurrency(Number(selectedOpp.expected_amount_cents || 0) / 100)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Stage</p>
              <Badge className="mt-1">{selectedOpp.stage}</Badge>
            </div>

            {/* Stage progression bar */}
            <div className="flex gap-1">
              {STAGES.map((s, i) => (
                <div key={s.key} className="flex-1">
                  <div className={`h-2 rounded ${
                    STAGES.findIndex(x => x.key === selectedOpp.stage) >= i
                      ? 'bg-primary' : 'bg-muted'
                  }`} />
                  <p className="text-[10px] text-muted-foreground mt-1 text-center">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="pt-4 space-y-2">
              <Button onClick={() => { setShowCommitment(true); }} className="w-full">
                <DollarSign className="h-4 w-4 mr-2" /> Record Commitment
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  const nextIdx = STAGES.findIndex(s => s.key === selectedOpp.stage) + 1;
                  if (nextIdx < STAGES.length) {
                    handleMoveStage(selectedOpp.id, STAGES[nextIdx].key);
                    setSelectedOpp(null);
                  }
                }}
              >
                <Send className="h-4 w-4 mr-2" /> Advance Stage
              </Button>
            </div>

            {/* Commitments for this opp */}
            {selectedOpp.commitments && selectedOpp.commitments.length > 0 && (
              <div className="pt-4">
                <p className="text-xs text-muted-foreground mb-2">COMMITMENTS</p>
                {selectedOpp.commitments.filter((c: any) => c.id).map((c: any) => (
                  <div key={c.id} className="p-3 bg-[#F0F0F0] rounded-lg mb-2">
                    <p className="text-sm text-[#242424]">{formatCurrency(Number(c.amount_cents || 0) / 100)}</p>
                    <Badge className="text-xs mt-1">{c.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CapitalRaiseDetail;
